import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormControl,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { NavigationExtras, Router } from "@angular/router";
import { Company, User } from "@core/models";
import { TrainSearchRequest } from "@core/models/trains/train-search-request";
import { TrainsInfo } from "@core/models/trainsInfo";
import { TrainStation } from "@core/models/trainStation";
import { AuthService, CompanyService, LanguageService, NgxToastrService, StateService, TrainService, TravelService, UserService, UtilsService } from "@core/services";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { CustomDateParserFormatter } from "@shared/component/date-parser";
import { DialogAssignProjectComponent } from "@shared/component/dialog-assign-project/dialog-assign-project.component";
import { AppState } from "app/app.reducer";
import moment from "moment";

@Component({
  selector: "app-train-search",
  templateUrl: "./train-search.component.html",
  styleUrls: ["./train-search.component.scss"],
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class TrainsSearchComponent implements OnInit, OnDestroy {
  @Input() projectDataSaved: any;
  @Input() searchHasFailed: boolean;
  @Input() travellers: any = [];

  @Output() emitProject = new EventEmitter<any>(); @Output() searchSubmitEmitter = new EventEmitter<any>();
  @Output() emitSelectedTravellersTrain: EventEmitter<any> = new EventEmitter<any>();

  companyUsers: User[] = [];
  date = new Date();
  defaultUser: any;
  filteredStationList: any = [];
  isRequest: boolean;
  isUser: boolean = false;
  loadingProjects: boolean = false;
  projects: any[];
  selectedProject: any = null;
  allowTrains: boolean = false;
  @Input() type = "train";
  trainsInfo: TrainsInfo;
  passengersModalOpen: boolean = false;
  public filterControl = new FormControl();
  public filteredUsers: User[];

  constructor(
    private dialogRef: MatDialogRef<TrainsSearchComponent>,
    public translate: TranslateService,
    private ngxToastrService: NgxToastrService,
    private utilsService: UtilsService,
    private stateService: StateService,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private companyService: CompanyService,
    private travelService: TravelService,
    private trainService: TrainService,
    private store: Store<AppState>,
    private authService: AuthService,
    private langService: LanguageService,
    private dialog: MatDialog,
  ) {
    this.utilsService.socket.on("search-outward", (res) => {
      this.searchOutwardResponse(res);
    });

    this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo) => {
      this.trainsInfo = info;
      this.travellers = this.trainsInfo?.searchConfig?.travellers
    });

    if (window.history.state.dataFromCompany) {
      const dataFromCompany = window.history.state.dataFromCompany;
      this.trainService.setTrainsInfoPartial({ companyUuid: dataFromCompany.companyUuid });
      this.trainService.setTrainsInfoPartial({ userUuid: dataFromCompany.userUuid });
      this.isUser = false;
    } else {
      const dataFromState = this.stateService.dataFromCompany$.getValue();
      if (dataFromState && dataFromState.userUuid) {
        this.trainService.setTrainsInfoPartial({ companyUuid: dataFromState.companyUuid });
        this.trainService.setTrainsInfoPartial({ userUuid: dataFromState.userUuid });
        this.isUser = true;
      }
    }

    if (!this.filteredUsers || this.filteredUsers.length <= 0) {
      this.getCompanyUsers();
    }
  }
  
  ngOnDestroy(): void {
    this.utilsService.socket.off('search-outward');
  }

  private getUserRole(user: any): string {
    return `${user?.accessType || user?.type || user?.user?.accessType || user?.user?.type || ""}`.toUpperCase();
  }

  private isPrivilegedUser(user: any): boolean {
    return ["COMPANY", "AGENCY", "HUB", "EMPLOYEE"].includes(this.getUserRole(user));
  }

  ngOnInit(): void {
    this.store.select("auth").subscribe((s: any) => {
      if (!s.user) {
        return;
      }

      const userRole = this.getUserRole(s.user);
      const isAdmin = this.isPrivilegedUser(s.user);
      this.trainService.setTrainsInfoPartial({
        isAdmin,
        isEmployee: userRole === "EMPLOYEE"
      });

      if (isAdmin) {
        this.trainService.setTrainsInfoPartial({
          userToBook: localStorage.getItem("userToBook")
            ? this.utilsService.decrypt(localStorage.getItem("userToBook"))
            : null
        });
        if (this.trainsInfo.userToBook != null) {
          this.trainService.setTrainsInfoPartial({ userUuid: this.trainsInfo.userToBook });
        }

        this.trainService.setTrainsInfoPartial({
          canUseTRM: s.user?.plan?.permissions?.canUseTRM
            ?? s.user?.user?.plan?.permissions?.canUseTRM
            ?? this.authService.userCompanyPlan?.permissions?.canUseTRM
        });

        if (!this.trainsInfo.canUseTRM) {
          this.trainService.setTrainsInfoPartial({ canUseTRM: this.authService.userCompanyPlan?.permissions?.canUseTRM });
        }
      } else {
        this.trainService.setTrainsInfoPartial({ companyUuid: s.user.companyUuid });
        this.trainService.setTrainsInfoPartial({ userUuid: s.user.uuid });
        this.isUser = true;
      }
      this.userService.getUser(this.trainsInfo.userUuid).subscribe((u) => {
        this.trainService.setTrainsInfoPartial({ user: u });
      })
      this.userService.getPoliciesUser(this.trainsInfo.userUuid).subscribe((g) => {
        g.filters.requestTrain
          ? (this.allowTrains = true)
          : (this.allowTrains = false);
        this.isRequest = g.filters.onlyTrainAuthorization;
        this.trainService.setTrainsInfoPartial({ userFilters: g.filters });

      })
    })

    if (window.history.state.dataFromCompany) {
      const dataFromCompany = window.history.state.dataFromCompany;
      this.trainService.setTrainsInfoPartial({ userUuid: dataFromCompany.userUuid });
      this.type = dataFromCompany.type;
      this.trainService.setTrainsInfoPartial({ companyUuid: dataFromCompany.companyUuid });
      this.isUser = false;
      this.stateService.setDataFromCompany(dataFromCompany);
    } else {
      const dataFromState = this.stateService.dataFromCompany$.getValue();
      if (dataFromState && dataFromState.userUuid) {
        this.trainService.setTrainsInfoPartial({ userUuid: dataFromState.userUuid });
        this.type = dataFromState.type;
        this.trainService.setTrainsInfoPartial({ companyUuid: dataFromState.companyUuid });
        this.isUser = true;
      }
    }
    if (this.trainsInfo.searchConfig == null) {
      this.trainService.setTrainsInfoPartial({
        searchConfig: {
          departDate: null,
          returnDate: null,
          travellers: [],
          origin: null,
          destination: null,
          oneWay: false,
          project: null
        }
      });
    }
    this.getTrainStations();
    this.getCardTypes();
    this.getCompanyUsers();
    this.utilsService.clearProcessedUuids();
    if (!this.trainsInfo.user) {
      this.userService.getUser(this.trainsInfo.userUuid).subscribe((u) => {
        this.trainService.setTrainsInfoPartial({ user: u });
      })
    }
    /* if (!this.travellers) {
      this.updatetravellers();
    } */
    if (!this.trainsInfo || !this.trainsInfo.formSearchTrain) {
      this.trainService.setTrainsInfoPartial({
        formSearchTrain: this.formBuilder.group({
          origin: [this.trainsInfo.searchConfig.origin, [Validators.required]],
          destination: [this.trainsInfo.searchConfig.destination, [Validators.required]],
          departDate: [this.trainsInfo.searchConfig.departDate, [Validators.required]],
          departTime: [null, Validators.required],
          returnDate: [this.trainsInfo.searchConfig.returnDate, []],
          returnTime: [null],
          travellers: [this.trainsInfo.searchConfig.travellers, [Validators.required]],
          selectedProject: [this.trainsInfo.searchConfig.project],
        })
      });
    }
    if (this.trainsInfo.searchConfig && this.trainsInfo.searchConfig.departDate != null) {
      this.setOneWaySearch(this.trainsInfo.searchConfig.oneWay);
    }

    /* if (this.projectDataSaved) {
      this.projects = this.projectDataSaved.allProjects;
      this.formSearchTrain.patchValue({
        selectedProject: this.projectDataSaved.selectedProject
      });
    } */
    this.projects = this.trainsInfo.projects ?? []
    this.selectedProject = this.trainsInfo.searchConfig?.project
  }

  optionSelected(field: string, value: string) {
    if (this.trainsInfo.trainStationList.length > 0) {
      if (field === "origin") {
        this.trainsInfo.searchConfig.origin = this.trainsInfo.trainStationList.find(
          (a) => a.name === value
        );
      }
      if (field === "destination") {
        this.trainsInfo.searchConfig.destination = this.trainsInfo.trainStationList.find(
          (a) => a.name === value
        );
      }
    }
  }

  switchOriginDestination() {
    let origin = this.trainsInfo.formSearchTrain.value.origin;
    let destination = this.trainsInfo.formSearchTrain.value.destination;
    this.trainsInfo.formSearchTrain.controls['origin'].setValue(destination)
    this.trainsInfo.formSearchTrain.controls['destination'].setValue(origin)
    origin = this.trainsInfo.searchConfig.origin
    destination = this.trainsInfo.searchConfig.destination
    this.trainsInfo.searchConfig.destination = origin
    this.trainsInfo.searchConfig.origin = destination
    this.trainService.setTrainsInfoPartial({ searchConfig: this.trainsInfo.searchConfig });
    this.trainService.setTrainsInfoPartial({ formSearchTrain: this.trainsInfo.formSearchTrain });
  }

  setOneWaySearch(set: boolean) {
    if (set) {
      this.trainsInfo.searchConfig.oneWay = true;
      this.trainsInfo.formSearchTrain.get("returnDate").clearValidators();
      this.trainsInfo.formSearchTrain.patchValue({ returnDate: null });
    } else {
      this.trainsInfo.searchConfig.oneWay = false;
      this.trainsInfo.formSearchTrain.get("returnDate").setValidators(Validators.required);
    }
    this.trainService.setTrainsInfoPartial({ searchConfig: this.trainsInfo.searchConfig });
    this.trainsInfo.formSearchTrain.get("returnDate").updateValueAndValidity();
    this.trainService.setTrainsInfoPartial({ formSearchTrain: this.trainsInfo.formSearchTrain });

  }

  getTrainStations() {
    this.trainService.setTrainsInfoPartial({ trainStationList: [] });
    this.trainService.getStations().subscribe((res: TrainStation[]) => {
      this.trainService.setTrainsInfoPartial({ trainStationList: res });
    })
  }

  getCardTypes() {
    this.trainsInfo.trainCards = [];
    this.trainService.getCardTypes().subscribe((res: any[]) => {
      this.trainsInfo.trainCards = res;
    })
  }

  selectCard(value: any, traveller: any) {
    traveller.selectedCard = value
  }

  displayStation = (station: any): string => {
    const s = this.filteredStationList.find(s => s.code === station.code);
    return s ? `${s.name} (${s.country})` : '';
  };

  getStations(value: string) {
    this.filteredStationList = [];
    if (value && value.length > 2) {
      const normalizedValue = value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      this.filteredStationList = this.trainsInfo.trainStationList.filter((e) => {
        const normalizedName = e.name
          ?.toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
        return normalizedName.includes(normalizedValue);
      }).sort((a, b) => a.name.length - b.name.length);
    }
  }

  onCardNumberChange(event: any, traveller: any) {
    traveller.selectedCard.number = event.target.value;
  }

  onSearch() {
    if (!this.trainsInfo.formSearchTrain.valid ||
      (!this.trainsInfo.searchConfig.oneWay &&
        (!this.trainsInfo.formSearchTrain.get('returnDate').value || !this.trainsInfo.formSearchTrain.get('returnTime').value))) {
      return
    }
    if (this.trainService.checkOriginOrDestinationMatch(this.trainsInfo) === false) {
      this.trainService.setTrainsInfoPartial({
        searchConfig: {
          departDate: this.trainsInfo.formSearchTrain.value.departDate,
          returnDate: this.trainsInfo.formSearchTrain.value.returnDate ? this.trainsInfo.formSearchTrain.value.returnDate : null,
          travellers: this.travellers,
          origin: this.trainsInfo.searchConfig.origin,
          destination: this.trainsInfo.searchConfig.destination,
          oneWay: this.trainsInfo.searchConfig.oneWay,
          project: this.trainsInfo.formSearchTrain.value.selectedProject
        }
      });
      this.trainService.setTrainsInfoPartial({ travellers: this.trainsInfo.formSearchTrain.value.travellers });
      /* const projectData = {
        selectedProject: this.selectedProject,
        allProjects: this.projects
      }; */
      this.trainService.setTrainsInfoPartial({ projects: this.projects })
      this.utilsService.loadingTrains = true;
      const correlation = crypto.randomUUID();
      const request: TrainSearchRequest = {
        language: this.langService.getLanguage(),
        origin: this.trainsInfo.searchConfig.origin,
        destination: this.trainsInfo.searchConfig.destination,
        departDate: this.utilsService.combineDateAndTime(this.trainsInfo.searchConfig.departDate, this.trainsInfo.formSearchTrain.value.departTime),
        oneWay: this.trainsInfo.searchConfig.oneWay != null ? this.trainsInfo.searchConfig.oneWay : false,
        userUuid: this.trainsInfo.userUuid,
        passengers: this.trainsInfo.searchConfig.travellers,
        correlationId: correlation,
      };
      this.trainService.addCorrelation(correlation);

      this.trainsInfo.searchConfig.oneWay = request.oneWay;
      this.trainService.setTrainsInfoPartial({ searchConfig: this.trainsInfo.searchConfig });


      if (this.trainsInfo.searchConfig.returnDate && !this.trainsInfo.searchConfig.oneWay) {
        request["returnDate"] = this.utilsService.combineDateAndTime(this.trainsInfo.searchConfig.returnDate, this.trainsInfo.formSearchTrain.value.returnTime);
      }

      this.trainService.searchTrainsOutward(request).subscribe(
        (s) => {
          this.trainsInfo.selectedTrains = null;
          this.trainsInfo.selectedExtras = null;
          this.trainsInfo.selectedSeats = null;
        },
        (err) => {
          this.utilsService.loadingTrains = false;
          this.searchHasFailed = true;
          if (err.status === 818) {
            this.translate
              .get(["train.notAvailable"])
              .subscribe((result) => {
                this.utilsService.loadingTrains = false;
                this.ngxToastrService.typeInfo(
                  "Error",
                  result["train.notAvailable"]
                );
              })
          } else {
            this.translate
              .get(["train.messages.error"])
              .subscribe((result) => {
                this.ngxToastrService.typeInfo(
                  "Error",
                  result["train.messages.error"]
                );
                this.utilsService.loadingTrains = false;
              })
          }
        }
      )
    }
  }

  goToCreateCustomTrain() {
    const navigationExtras: NavigationExtras = {
      state: {
        canBookHotel: this.trainsInfo.userFilters.requestBooked,
        canRequestCustomBooking: this.trainsInfo.userFilters.requestCustomBooking,
        canRequestCustomFlight: this.trainsInfo.userFilters.requestCustomFlight,
        canRequestCustomTrain: this.trainsInfo.userFilters.requestCustomTrain,
        canRequestCustomCar: this.trainsInfo.userFilters.requestCar,
        canRequestAirbnb: this.trainsInfo.userFilters.requestCustomAirbnb,
        userUuid: this.trainsInfo.userToBook ? this.trainsInfo.userToBook : this.trainsInfo.userUuid,
        companyUuid: this.trainsInfo.companyUuid,
        userImageBase64: this.trainsInfo.user.image,
        isFromSearchFail: true,
        searchFailData: this.trainsInfo.searchConfig,
      },
    };
    this.router.navigate(["/custom-services/train"], navigationExtras);
  }

  getCompanyUsers() {
    if (!this.trainsInfo?.companyUuid && (this.trainsInfo?.userUuid || this.trainsInfo?.userToBook)) {
      this.userService.getUser((this.trainsInfo?.userUuid || this.trainsInfo?.userToBook)).subscribe(
        (res) => {
          this.trainService.setTrainsInfoPartial({ companyUuid: res.companyUuid });
          this.fetchCompanyUsers();
        },
        (err) => { }
      );
    } else {
      this.fetchCompanyUsers();
    }
  }

  fetchCompanyUsers() {
    if (this.trainsInfo?.companyUuid) {
      if (!this.trainsInfo.company) {
        this.companyService.getCompany(this.trainsInfo?.companyUuid).subscribe(
          (company: Company) => {
            this.trainsInfo.company = company;
          },
          (error) => { }
        );

      }
      this.companyService.getCompanyUsers(this.trainsInfo.companyUuid).subscribe(
        (res) => {
          this.companyUsers = res;
          this.filteredUsers = [...this.companyUsers];
          this.setInitialtraveller();
        },
        (err) => { }
      );
    }
  }

  setInitialtraveller() {
    const user = this.companyUsers.find((user) => user.uuid === this.trainsInfo.userUuid);
    if (user) {
      this.defaultUser = user;
      if (!this.travellers.find((t) => t.uuid === this.trainsInfo.userUuid)) {
        this.travellers.push(this.defaultUser)
        this.trainsInfo.formSearchTrain.controls["travellers"].setValue(this.travellers)
        this.trainService.setTrainsInfoPartial({ formSearchTrain: this.trainsInfo.formSearchTrain });

      }
    }
    /*     if (!this.travellersSelected) {
          this.updatetravellers();
        } */
  }

  changeTravellers(operation: string) {
    if (operation === 'add') {
      this.travellers.push('')
      this.trainsInfo.formSearchTrain.controls["travellers"].setValue(this.travellers)
    } else if (operation === 'remove') {
      this.travellers.pop()
      this.trainsInfo.formSearchTrain.controls["travellers"].setValue(this.travellers)
    }
    this.trainService.setTrainsInfoPartial({ formSearchTrain: this.trainsInfo.formSearchTrain });

  }

  /*   updatetravellers() {
      const user = this.companyUsers.find((user) => user.uuid === this.userUuid);
      if (user) {
        this.defaultUser = user;
      }
      for (let i = this.travellers.length; i < this.searchConfig.travellers.length - 1; i++) {
        if (!this.travellers[i]) {
          this.travellers[i] = "";
        }
      }
      if (this.travellers.length > this.searchConfig.travellers.length - 1) {
        this.travellers = this.travellers.slice(0, this.searchConfig.travellers.length - 1);
      }
      setTimeout(() => {
        this.travellers = [...this.travellers];
      });
    } */

  applyFilter(filterValue: string) {
    this.filteredUsers = this.companyUsers.filter(user =>
      `${user.name} ${user.lastname}`.toLowerCase().includes(filterValue.toLowerCase())
    );
  }

  selectFirstFilteredUser(event: KeyboardEvent, index: number, select: MatSelect) {
    event.stopPropagation();
    event.preventDefault();

    const user = this.filteredUsers?.[0];
    if (!user) {
      return;
    }

    this.travellers[index] = user;
    select.close();
  }

  getUserName(uuid: string): string {
    const user = this.companyUsers.find(u => u.uuid === uuid);
    return user ? `${user.name} ${user.lastname}` : '';
  }

  onPanelClose() {
    this.filterControl.setValue('');
    this.filteredUsers = this.companyUsers;
  }

  onOpenedChange(event: boolean, input: HTMLInputElement) {
    if (event) {
      input.focus();
    }
  }

  alltravellersSelected(): boolean {
    return this.travellers.every(traveller => traveller !== null && traveller !== undefined && traveller !== '');
  }

  trackByIndex(index: number) {
    return index;
  }

  onDateChange() {
    if (!this.trainsInfo.searchConfig.oneWay) {
      if (this.trainsInfo.formSearchTrain.get('departDate').value && this.trainsInfo.formSearchTrain.get('returnDate').value) {
        const checkInParam = this.transformDateToISO(this.trainsInfo.formSearchTrain.get('departDate').value);
        const checkOutParam = this.transformDateToISO(this.trainsInfo.formSearchTrain.get('returnDate').value);
        this.loadProjects(checkInParam, checkOutParam);
      }
    } else {
      if (this.trainsInfo.formSearchTrain.get('departDate').value) {
        const checkInParam = this.transformDateToISO(this.trainsInfo.formSearchTrain.get('departDate').value);
        this.loadProjects(checkInParam);
      }
    }
  }

  transformDateToISO(date: { year: number, month: number, day: number }): string {
    const { year, month, day } = date;
    const jsDate = new Date(Date.UTC(year, month - 1, day));
    const datePart = jsDate.toISOString().split('T')[0];
    return `${datePart}T00:00:00.000+00:00`;
  }

  loadProjects(checkIn: any, checkOut?: any) {
    this.loadingProjects = true;
    this.travelService.getTravelsWithParams({ companyUuid: this.trainsInfo.companyUuid })
      .subscribe(resp => {
        this.projects = [];
        const defaultProject = {
          title: "--------No-------- ",
          uuid: "none",
        };

        this.projects.push(defaultProject);
        this.projects = this.projects.concat(resp.docs);

        this.projects = this.projects.filter((project) => {
          if (project.initDate && project.endDate) {
            const projectDatesAreCorrect = this.checkDatesAreCorrect(
              checkIn,
              checkOut,
              moment(project.initDate),
              moment(project.endDate)
            );
            return projectDatesAreCorrect;
          } else {
            return true;
          }
        });

        this.selectedProject = this.projects.find(project => project.uuid === 'none');

        this.loadingProjects = false;
      }, error => {
        this.loadingProjects = false;
      });
  }

  checkDatesAreCorrect(
    bookingInitDate,
    bookingEndDate,
    projectInitDate,
    projectEndDate
  ) {
    let projectDatesAreCorrect = true;
    if (bookingInitDate != null) {
      const momentInitDate = moment(bookingInitDate);
      projectDatesAreCorrect = moment(projectInitDate).isSameOrBefore(
        momentInitDate,
        "day"
      );
      if (bookingEndDate != null && projectDatesAreCorrect) {
        const momentEndDate = moment(bookingEndDate);
        projectDatesAreCorrect = moment(projectEndDate).isSameOrAfter(
          momentEndDate,
          "day"
        );
      } else {
        if (bookingEndDate == null && projectDatesAreCorrect) {
          projectDatesAreCorrect = moment(projectEndDate).isSameOrAfter(
            momentInitDate,
            "day"
          );
        }
      }
    }
    return projectDatesAreCorrect;
  }

  getOutMinDate() {
    const minDateWithAdvance = this.getInMinDate();
    return this.utilsService.getOutDateSame(
      minDateWithAdvance,
      this.trainsInfo.formSearchTrain.controls["departDate"].value
    );
  }

  getInMinDate() {
    const daysInAdvance = this.trainsInfo.isAdmin ? 0 : (this.trainsInfo.userFilters?.trainDaysInAdvance || 0);
    const minDate = moment().add(daysInAdvance, 'days');
    return {
      year: minDate.year(),
      month: minDate.month() + 1,
      day: minDate.date(),
    };
  }

  togglePassengersModal() {
    this.passengersModalOpen = !this.passengersModalOpen;
  }

  closePassengersModal() {
    this.passengersModalOpen = false;
  }

  searchOutwardResponse(res: any) {
    if (this.trainService.existsCorrelation(res.correlationId)) {
      this.trainService.removeCorrelation(res.correlationId)
      this.utilsService.loadingTrains = false;
      if ((res.data.type && (res.data.type?.toLowerCase().includes("error") || res.data.type?.toLowerCase().includes("exception"))) || (res.data.bookingStatus && (res.data.bookingStatus?.toLowerCase().includes('unknown') || res.data.bookingStatus?.toLowerCase().includes('error')))) {
        this.searchHasFailed = true;
        if (res.data?.status === 818) {
          this.translate
            .get(["train.notAvailable"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "Error",
                result["train.notAvailable"]
              );
            })
        } else if (res?.data?.message?.toLowerCase().includes('invalid card') && this.trainsInfo.searchConfig.travellers.some((t: any) => t.selectedCard)) {
          this.translate
            .get(["train.invalid-card"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                'Error',
                'train.invalid-card'
              );
            })
        } else {
          this.translate
            .get(["train.messages.error"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "Error",
                result["train.messages.error"]
              );
            })
        }
      } else {
        console.log(res)
        if (!res.data.outward || res.data.outward.length <= 0) {
          this.translate
            .get(["train.search-result.no-trains"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "Error",
                result["train.search-result.no-trains"]
              );
            })
        } else {
          this.trainsInfo.providerContextOutwardId = res.data.context.providerRequestId;
          this.trainService.setTrainsInfoPartial({ trainsList: res?.data });
          if (!this.trainsInfo.selectedTrains) {
            this.trainService.selectTrain(this.trainsInfo.trainsList?.outward[0], 'outward', this.trainsInfo, 
              this.trainsInfo.trainsList?.outward[0].sections.map((s) => s.alternatives[0].alternativeId))
          }
          if (!this.trainsInfo.isModal) {
            this.router.navigate(["train/search-result"]);
          } else {
            this.dialogRef.close();
          }
        }
      }
    }
  }

  goBack() {
    if (!this.stateService.didFirstSearch) {
      this.stateService.didFirstSearch = false;
      this.router.navigate([`/companies/${this.trainsInfo.companyUuid}`])
    } else {
      this.stateService.didFirstSearch = false;
    }
  }

  compareUsers(user1: any, user2: any): boolean {
    return user1?.uuid === user2?.uuid;
  }

  openTravelDialog() {
    const modalRef = this.dialog.open(DialogAssignProjectComponent, {
      data: {
        create: true,
        isOnlyCreate: true,
        userUuid: this.trainsInfo.userUuid,
        companyUuid: this.trainsInfo.companyUuid,
      },
      panelClass: "new-dialog",
    });

    modalRef.afterClosed().subscribe((result) => {
      if (result != null) {
        if (this.trainsInfo.userUuid !== null) {
          result.travelData["userUuid"] = this.trainsInfo.userUuid;
        }

        if (this.trainsInfo.companyUuid !== null) {
          result.travelData["companyUuid"] = this.trainsInfo.companyUuid;
        }
        if (
          result.travelData["endDate"] === "" &&
          result.travelData["initDate"] === ""
        ) {
          delete result.travelData["endDate"];
          delete result.travelData["initDate"];
        }
        this.travelService.postTravel(result.travelData).subscribe(
          (res) => {

            let dateIn = this.trainsInfo.formSearchTrain.get('departDate').value;
            let dateOut = this.trainsInfo.formSearchTrain.get('returnDate').value || null;

            const checkInFormat = moment({
              year: dateIn.year,
              month: dateIn.month - 1,
              day: dateIn.day
            });

            let checkOutFormat = null;
            if (dateOut) {
              checkOutFormat = moment({
                year: dateOut.year,
                month: dateOut.month - 1,
                day: dateOut.day
              });
            }

            const projectInitDate = moment(res.initDate);
            const projectEndDate = moment(res.endDate);

            if (!checkOutFormat) {
              const isWithinRange = checkInFormat.isBetween(projectInitDate, projectEndDate, null, '[]');
              if (isWithinRange || (!res.initDate && !res.endDate)) {
                this.projects.push(res);
                this.selectedProject = res;
              }
            } else {
              const isWithinRange = checkInFormat.isBetween(projectInitDate, projectEndDate, null, '[]') &&
                checkOutFormat.isBetween(projectInitDate, projectEndDate, null, '[]');
              if (isWithinRange || (!res.initDate && !res.endDate)) {
                this.projects.push(res);
                this.selectedProject = res;
              }
            }

            this.translate
              .get(`project.my-projects.created`)
              .subscribe((resp) => {
                this.ngxToastrService.typeSuccess(null, resp);
                setTimeout(() => {
                }, 3000);
              })
          },
          (err) => {
            this.translate.get("common.error").subscribe((resp) => {
              this.ngxToastrService.typeInfo(resp, err.error.message);
            })
          }
        )
      }
    });
  }
}
